import {
  buildCreateChildCommand,
  buildMoveSubtreeCommand,
  DEFAULT_NEW_NODE_SIZE,
  getSubtreeIds,
  planChildLayout,
  resolveMoveCommandCollisions,
} from './mapCommands';

export const IDEA_INBOX_STATE = 'inbox';

export function extractIdeaTags(text) {
  const matches = String(text || '').match(/#[\w-]+/g) || [];
  return [...new Set(matches.map((tag) => tag.slice(1).toLowerCase()).filter(Boolean))];
}

export function isInboxRoot(node) {
  return node?.inbox?.state === IDEA_INBOX_STATE;
}

export function deriveIdeaInbox(nodes, relationships) {
  const safeNodes = Array.isArray(nodes) ? nodes : [];
  const safeRelationships = Array.isArray(relationships) ? relationships : [];
  const nodesById = new Map(safeNodes.map((node) => [node.id, node]));
  const rootNodes = safeNodes
    .filter(isInboxRoot)
    .sort((left, right) => (
      String(right.inbox?.addedAt || '').localeCompare(String(left.inbox?.addedAt || ''))
      || String(left.id).localeCompare(String(right.id))
    ));
  const hiddenNodeIds = new Set();
  const branchCountByRootId = new Map();
  const childrenByParentId = new Map();
  safeRelationships.forEach((relationship) => {
    if (!relationship?.parentId || !relationship?.childId) return;
    const children = childrenByParentId.get(relationship.parentId) || [];
    children.push(relationship.childId);
    childrenByParentId.set(relationship.parentId, children);
  });

  rootNodes.forEach((rootNode) => {
    const branchIds = [];
    const visited = new Set();
    const pending = [rootNode.id];
    for (let index = 0; index < pending.length; index += 1) {
      const nodeId = pending[index];
      if (!nodeId || visited.has(nodeId) || !nodesById.has(nodeId)) continue;
      visited.add(nodeId);
      branchIds.push(nodeId);
      pending.push(...(childrenByParentId.get(nodeId) || []));
    }
    branchIds.forEach((nodeId) => hiddenNodeIds.add(nodeId));
    branchCountByRootId.set(rootNode.id, Math.max(0, branchIds.length - 1));
  });

  return {
    count: rootNodes.length,
    rootNodes,
    hiddenNodeIds,
    branchCountByRootId,
  };
}

function inboxMetadata({ now, previousParentId = null }) {
  return {
    state: IDEA_INBOX_STATE,
    addedAt: typeof now === 'string' ? now : new Date(now || Date.now()).toISOString(),
    previousParentId,
  };
}

export function buildCreateInboxIdeaCommand({
  id,
  text,
  info = '',
  color = null,
  userId = null,
  creatorName = null,
  now = null,
}) {
  if (!id) throw new Error('An inbox idea id is required');
  const normalizedText = String(text || '').trim();
  if (!normalizedText) throw new Error('Write an idea before adding it');

  return {
    type: 'create-inbox-idea',
    node: {
      id,
      text: normalizedText,
      info: String(info || ''),
      position: { x: 0, y: 0 },
      layoutSize: DEFAULT_NEW_NODE_SIZE,
      layout: { side: 'right', order: 0, pinned: false },
      color,
      userId,
      creatorName,
      isRoot: false,
      collapsedChildren: null,
      embeddedMindmapId: null,
      linkUrl: null,
      linkMeta: null,
      isTask: false,
      taskDone: false,
      emoji: null,
      attachment: null,
      isEmbeddedMindmapLink: false,
      tags: extractIdeaTags(normalizedText),
      inbox: inboxMetadata({ now }),
    },
  };
}

export function buildCaptureChildIdeaCommand({ text, info = '', ...options }) {
  const normalizedText = String(text || '').trim();
  if (!normalizedText) throw new Error('Write an idea before adding it');
  const command = buildCreateChildCommand(options);
  return {
    ...command,
    type: 'capture-child-idea',
    node: {
      ...command.node,
      text: normalizedText,
      info: String(info || ''),
      tags: extractIdeaTags(normalizedText),
      autoFocus: false,
    },
  };
}

export function buildMoveBranchToInboxCommand({
  nodeId,
  nodes,
  relationships,
  now = null,
}) {
  const safeNodes = Array.isArray(nodes) ? nodes : [];
  const safeRelationships = Array.isArray(relationships) ? relationships : [];
  const node = safeNodes.find((candidate) => candidate.id === nodeId);
  if (!node) throw new Error(`Idea ${nodeId} does not exist`);
  if (node.isRoot) throw new Error('The main idea cannot be moved to the inbox');
  if (isInboxRoot(node)) throw new Error('That idea is already in the inbox');
  const parentRelationships = safeRelationships.filter((relationship) => relationship.childId === nodeId);
  if (parentRelationships.length !== 1) {
    throw new Error('Only an attached idea can be moved to the inbox');
  }
  const previousParentId = parentRelationships[0].parentId;

  return {
    type: 'move-branch-to-inbox',
    nodeId,
    previousParentId,
    removeRelationship: { parentId: previousParentId, childId: nodeId },
    inbox: inboxMetadata({ now, previousParentId }),
  };
}

export function buildDeleteInboxBranchCommand({
  nodeId,
  nodes,
  relationships,
  actorUserId = null,
}) {
  const safeNodes = Array.isArray(nodes) ? nodes : [];
  const safeRelationships = Array.isArray(relationships) ? relationships : [];
  const nodesById = new Map(safeNodes.map((node) => [node.id, node]));
  const node = nodesById.get(nodeId);
  if (!node || !isInboxRoot(node)) throw new Error('That idea is no longer in the inbox');
  if (safeRelationships.some((relationship) => relationship.childId === nodeId)) {
    throw new Error('That idea has already been placed');
  }
  const nodeIds = getSubtreeIds(nodeId, safeRelationships);
  const subtreeNodes = nodeIds.map((id) => nodesById.get(id));
  if (subtreeNodes.some((candidate) => !candidate)) {
    throw new Error('The branch contains a relationship to a missing idea');
  }
  if (actorUserId) {
    const foreignNode = subtreeNodes.find((candidate) => (
      candidate.userId && candidate.userId !== actorUserId
    ));
    if (foreignNode) {
      throw new Error('This branch contains ideas created by another collaborator');
    }
  }
  const deletingIds = new Set(nodeIds);
  return {
    type: 'delete-subtree',
    nodeId,
    nodeIds,
    parentId: null,
    nextSelectedId: null,
    removedRelationships: safeRelationships.filter((relationship) => (
      deletingIds.has(relationship.parentId) || deletingIds.has(relationship.childId)
    )),
  };
}

export function buildAttachInboxBranchCommand({
  nodeId,
  parentId,
  nodes,
  relationships,
  nodeSizes = null,
  usableViewport = null,
}) {
  const safeNodes = Array.isArray(nodes) ? nodes : [];
  const safeRelationships = Array.isArray(relationships) ? relationships : [];
  const nodesById = new Map(safeNodes.map((node) => [node.id, node]));
  const node = nodesById.get(nodeId);
  const parent = nodesById.get(parentId);
  if (!node || !isInboxRoot(node)) throw new Error('That idea is not in the inbox');
  if (!parent || isInboxRoot(parent)) throw new Error('Choose an idea in the map');
  if (nodeId === parentId) throw new Error('An idea cannot contain itself');
  if (safeRelationships.some((relationship) => relationship.childId === nodeId)) {
    throw new Error('That inbox idea has already been placed');
  }
  const subtreeIds = new Set(getSubtreeIds(nodeId, safeRelationships));
  if (subtreeIds.has(parentId)) throw new Error('Placing that branch would create a cycle');

  const inbox = deriveIdeaInbox(safeNodes, safeRelationships);
  const stationaryNodes = safeNodes.filter((candidate) => (
    !subtreeIds.has(candidate.id) && !inbox.hiddenNodeIds.has(candidate.id)
  ));
  const stationaryRelationships = safeRelationships.filter((relationship) => (
    !subtreeIds.has(relationship.parentId)
    && !subtreeIds.has(relationship.childId)
    && !inbox.hiddenNodeIds.has(relationship.parentId)
    && !inbox.hiddenNodeIds.has(relationship.childId)
  ));
  const nodeSize = nodeSizes instanceof Map ? nodeSizes.get(nodeId) : node.layoutSize;
  const targetLayout = planChildLayout({
    parentId,
    nodes: stationaryNodes,
    relationships: stationaryRelationships,
    size: nodeSize || DEFAULT_NEW_NODE_SIZE,
    nodeSizes,
    usableViewport,
  });
  const movement = buildMoveSubtreeCommand({
    nodeId,
    targetPosition: targetLayout.position,
    nodes: safeNodes,
    relationships: safeRelationships,
  });
  const movementWithLayout = {
    ...movement,
    positions: movement.positions.map((entry) => (
      entry.id === nodeId ? { ...entry, layout: targetLayout.layout } : entry
    )),
  };
  const resolved = resolveMoveCommandCollisions(movementWithLayout, {
    nodes: stationaryNodes.concat(
      safeNodes.filter((candidate) => subtreeIds.has(candidate.id))
    ),
    nodeSizes,
  });
  return {
    ...resolved,
    type: 'attach-inbox-branch',
    parentId,
    relationship: { parentId, childId: nodeId },
  };
}
